import { DefaultButton, IconButton as FluentIconButton, FocusTrapZone, Icon, Layer, mergeStyleSets, Popup } from "@fluentui/react";
import { Tooltip } from "@fluentui/react-components";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ShareIcon from "@mui/icons-material/Share";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Menu, MenuItem } from "@mui/material";
import React, { useState } from "react";
import { deleteSessionApi, saveSharedChatValueApi, SessionInfo } from "../../api";
import { userInfo } from "../../authConfig";
import i18next from "../../i18n/i18n";
import { SourcePage } from "./SessionNotFound";
import { useSelector } from "react-redux";
import { RootState } from "../../Store";

// UTM parameters configuration for share links
const UTM_PARAMETERS = {
    source: "aiworks",
    medium: "share_link",
    campaign: "chat_share"
} as const;

const popupStyles = mergeStyleSets({
    root: {
        background: "rgba(0, 0, 0, 0.5)",
        bottom: "0",
        left: "0",
        position: "absolute",
        right: "0",
        top: "0"
    },
    content: {
        background: "white",
        left: "50%",
        maxWidth: "1000px",
        position: "absolute",
        top: "50%",
        transform: "translate(-50%, -50%)",
        borderRadius: "10px",
        border: "2px solid",
        borderColor: "grey",
        padding: "15px"
    }
});

interface SessionActionsMenuProps {
    session: SessionInfo;
    isVisible: boolean;
    onDeleteSuccess?: () => void;
    showToast: (message: string, severity?: "success" | "error" | "info" | "warning") => void;
    isDeleting: boolean;
    setDeletingSessionId: (sessionId: string | null) => void;
    sourcePage: SourcePage;
}

export const SessionActionsMenu: React.FC<SessionActionsMenuProps> = ({
    session,
    isVisible,
    onDeleteSuccess,
    showToast,
    isDeleting,
    setDeletingSessionId,
    sourcePage
}) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const fileUploadOrDeleteAPITriggerActive = useSelector((store: RootState) => store.questionInput.fileUploadOrDeleteAPITriggerActive);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        if (isDeleting || fileUploadOrDeleteAPITriggerActive?.triggerActive) return; // Prevent menu opening during delete
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleMenuItemClick = (action: "delete" | "share") => {
        handleMenuClose();
        if (action === "delete") {
            setDeleteDialogOpen(true);
        } else if (action === "share") {
            setShareDialogOpen(true);
        }
    };

    const handleDeleteDialogClose = (event?: any, reason?: string) => {
        // Prevent any event bubbling
        event?.stopPropagation?.();
        event?.preventDefault?.();

        setDeleteDialogOpen(false);
    };

    const handleShareDialogClose = () => {
        setShareDialogOpen(false);
    };

    const handleDeleteCancel = (event: React.MouseEvent<HTMLButtonElement>) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();
        handleDeleteDialogClose(event);
    };

    const handleDeleteConfirm = async (event: React.MouseEvent<HTMLButtonElement>) => {
        // Stop event propagation to prevent triggering parent Tab onClick
        event?.stopPropagation?.();
        event?.preventDefault?.();

        const sessionId = session.session_id;

        if (sessionId) {
            try {
                setDeletingSessionId(sessionId); // Set loading state for this specific session
                const response = await deleteSessionApi(sessionId);

                if (!response.ok) {
                    console.error("Failed to delete session. Status:", response.status);
                    const errorResult = await response.json();
                    showToast(errorResult.error || i18next.t("sidebar.deleteFailed"), "error");
                    setDeletingSessionId(null); // Reset loading state on error
                    return;
                }

                const result = await response.json();
                // Show success notification
                showToast(i18next.t("sidebar.deleteSuccess"), "success");
                setDeleteDialogOpen(false);

                // Call success callback if provided (this will also reset isDeleting in Sidebar)
                if (onDeleteSuccess) {
                    onDeleteSuccess();
                }
            } catch (error) {
                console.error("Error deleting session:", error);
                showToast(i18next.t("sidebar.deleteFailed"), "error");
                setDeletingSessionId(null);
                setDeleteDialogOpen(false);
            }
        }
    };

    // Copy to clipboard functionality with error handling
    const copyToClipboard = async (href: string): Promise<boolean> => {
        try {
            // Add UTM parameters to the chat share link
            const url = new URL(href);
            url.searchParams.set("utm_source", UTM_PARAMETERS.source);
            url.searchParams.set("utm_medium", UTM_PARAMETERS.medium);
            url.searchParams.set("utm_campaign", UTM_PARAMETERS.campaign);
            const chatShareLink = url.toString();

            // Use modern clipboard API if available
            if (navigator.clipboard) {
                await navigator.clipboard.writeText(chatShareLink);
                return true;
            } else {
                // Fallback for older browsers
                const tempInput = document.createElement("input");
                tempInput.value = chatShareLink;
                tempInput.style.position = "fixed";
                tempInput.style.opacity = "0";
                document.body.appendChild(tempInput);
                tempInput.select();
                tempInput.setSelectionRange(0, tempInput.value.length);
                const success = document.execCommand("copy");
                document.body.removeChild(tempInput);
                return success;
            }
        } catch (error) {
            console.error("Failed to copy to clipboard:", error);
            return false;
        }
    };

    // Save shared chat value with error handling
    const saveSharedChatValue = async (sessionValue: string): Promise<boolean> => {
        try {
            const response = await saveSharedChatValueApi(sessionValue, userInfo.corp_id);
            if (!response.ok) {
                console.error("Failed to save shared chat value. Status:", response.status);
                return false;
            }

            const result = await response.json();
            return true;
        } catch (error) {
            console.error("Error saving shared chat value:", error);
            return false;
        }
    };

    // Handle share action
    const handleShareAction = async () => {
        const sessionId = session.session_id;
        if (sessionId) {
            try {
                // Build share URL with session ID
                const baseUrl = window.location.origin + window.location.pathname;
                const shareUrl = `${baseUrl}?SessionId=${sessionId}`;

                const copySuccess = await copyToClipboard(shareUrl);
                setShareDialogOpen(false);

                if (copySuccess) {
                    showToast(i18next.t("answer.chatLinkCopied"), "success");
                    // Save shared chat value after successful copy
                    await saveSharedChatValue(sessionId);
                } else {
                    showToast(i18next.t("answer.shareFailed"), "error");
                }
            } catch (error) {
                console.error("Error during share operation:", error);
                setShareDialogOpen(false);
                showToast(i18next.t("answer.shareFailed"), "error");
            }
        } else {
            setShareDialogOpen(false);
            showToast(i18next.t("answer.shareFailed"), "error");
        }
    };

    const ShareComponent: React.FC<{ isVisible: boolean }> = ({ isVisible }) => {
        if (!isVisible) {
            return null;
        }
        return (
            <Layer>
                <Popup className={popupStyles.root} role="dialog" aria-modal="true" onDismiss={handleShareDialogClose}>
                    <FocusTrapZone>
                        <div role="document" className={popupStyles.content} style={{ width: "500px" }}>
                            <div style={{ padding: "5px", fontSize: "15px", fontWeight: "bold" }}>
                                <Icon iconName="share" style={{ padding: "5px", fontSize: "20px" }} />
                                <span style={{ fontSize: "19px" }}>{i18next.t("answer.shareChat")}</span>
                                <FluentIconButton
                                    style={{ color: "black", float: "right" }}
                                    iconProps={{ iconName: "Cancel" }}
                                    onClick={handleShareDialogClose}
                                />
                            </div>
                            <div style={{ display: "grid", justifyContent: "center", width: "465px", padding: "20px 0" }}>
                                <label style={{ fontSize: "20px" }}>{i18next.t("answer.shareChatText")}</label>
                            </div>
                            <div style={{ display: "flex", justifyContent: "center" }}>
                                <DefaultButton
                                    autoFocus
                                    iconProps={{ iconName: "link" }}
                                    style={{ color: "#1976d2", borderRadius: "10px", padding: "5px", fontSize: "13px" }}
                                    onClick={handleShareAction}
                                    text={i18next.t("answer.copyLink")}
                                />
                            </div>
                        </div>
                    </FocusTrapZone>
                </Popup>
            </Layer>
        );
    };

    return (
        <>
            <Tooltip
                content={{
                    children: i18next.t("sidebar.moreActions"),
                    style: {
                        backgroundColor: "white",
                        color: "black"
                    }
                }}
                relationship="label"
            >
                <IconButton
                    size="small"
                    onClick={handleMenuOpen}
                    disabled={isDeleting || fileUploadOrDeleteAPITriggerActive?.triggerActive}
                    sx={{
                        padding: "2px",
                        color: "inherit",
                        flexShrink: 0,
                        position: "absolute",
                        right: "8px",
                        visibility: isVisible ? "visible" : "hidden",
                        opacity: isDeleting ? 0.5 : 1,
                        cursor: isDeleting || fileUploadOrDeleteAPITriggerActive?.triggerActive ? "not-allowed" : "pointer",
                        "&:hover": {
                            backgroundColor: isDeleting ? "transparent" : "rgba(0, 0, 0, 0.1)"
                        }
                    }}
                >
                    {isDeleting ? <CircularProgress size={16} sx={{ color: "#d32f2f" }} /> : <MoreVertIcon fontSize="small" />}
                </IconButton>
            </Tooltip>

            {/* Context Menu for Session Options */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={(event: React.SyntheticEvent) => {
                    event.stopPropagation();
                    event.preventDefault();
                    handleMenuClose();
                }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right"
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right"
                }}
                sx={{
                    "& .MuiPaper-root": {
                        fontSize: "14px",
                        minWidth: "140px"
                    }
                }}
            >
                {sourcePage === "chat" && (
                    <MenuItem
                        onClick={() => handleMenuItemClick("delete")}
                        disabled={isDeleting || fileUploadOrDeleteAPITriggerActive?.triggerActive}
                        sx={{
                            fontSize: "14px",
                            padding: "6px 12px",
                            minHeight: "auto",
                            cursor: isDeleting || fileUploadOrDeleteAPITriggerActive?.triggerActive ? "not-allowed" : "pointer"
                        }}
                    >
                        <DeleteIcon fontSize="small" sx={{ mr: 1, fontSize: "16px" }} />
                        {i18next.t("sidebar.delete")}
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => handleMenuItemClick("share")}
                    disabled={isDeleting || fileUploadOrDeleteAPITriggerActive?.triggerActive}
                    sx={{
                        fontSize: "14px",
                        padding: "6px 12px",
                        minHeight: "auto",
                        cursor: isDeleting || fileUploadOrDeleteAPITriggerActive?.triggerActive ? "not-allowed" : "pointer"
                    }}
                >
                    <ShareIcon fontSize="small" sx={{ mr: 1, fontSize: "16px" }} />
                    {i18next.t("sidebar.share")}
                </MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={(event: React.SyntheticEvent, reason?: string) => {
                    // Prevent closing on backdrop click or escape key
                    if (reason === "backdropClick" || reason === "escapeKeyDown") {
                        event?.stopPropagation?.();
                        event?.preventDefault?.();
                        return; // Don't close the dialog
                    }
                    handleDeleteDialogClose(event, reason);
                }}
                onClick={e => {
                    e.stopPropagation();
                }}
                disableEscapeKeyDown={true}
                disableScrollLock={true}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                slotProps={{
                    backdrop: {
                        onClick: e => {
                            e.stopPropagation();
                            e.preventDefault();
                        }
                    }
                }}
            >
                <DialogTitle id="delete-dialog-title">{i18next.t("sidebar.deleteDialog.title")}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">{i18next.t("sidebar.deleteDialog.message")}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDeleteCancel}
                        variant="text"
                        autoFocus={true}
                        disabled={isDeleting || fileUploadOrDeleteAPITriggerActive?.triggerActive}
                        sx={{
                            color: "#7376e1",
                            cursor: isDeleting || fileUploadOrDeleteAPITriggerActive?.triggerActive ? "not-allowed" : "pointer",
                            "&:hover": {
                                backgroundColor: "rgba(115, 118, 225, 0.04)"
                            }
                        }}
                    >
                        {i18next.t("sidebar.cancel")}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="text"
                        disabled={isDeleting || fileUploadOrDeleteAPITriggerActive?.triggerActive}
                        sx={{
                            color: "#d32f2f",
                            cursor: isDeleting || fileUploadOrDeleteAPITriggerActive?.triggerActive ? "not-allowed" : "pointer",
                            "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 0.04)"
                            }
                        }}
                    >
                        {i18next.t("sidebar.delete")}
                    </Button>
                </DialogActions>
            </Dialog>
            <ShareComponent isVisible={shareDialogOpen} />
        </>
    );
};
